/* eslint-disable complexity */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { copyToClipboard } from '@scale/frontend-lib/clipboard';
import { TaskType } from '@scale/shared-types';
import { IProjectRole, Role } from '@scale/shared/corp/projectRoles';
import { IProjectGroup } from '@scale/shared/dailyPlan/types/api';
import { ProjectType } from '@scale/shared/defaults';
import { QMListProjectsShadowProjectRequest, QMProject } from '@scale/shared/qm/qmRequestTypes';
import { ListTeamsByProjectsResponse } from '@scale/shared/types/api/corp-api/workerTeams';
import { IFindPermissionGroupsResponse } from '@scale/shared/types/permissiongroup';
import { IUserJson } from '@scale/shared/types/user';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import ProductPill from 'corp/components/UI/ProductPill';
import SelectProject from 'corp/components/UI/SelectProject';
import { Button, CircularProgress, IconButton, Tooltip } from 'design-system';
import { H1, H3, Text } from 'design-system/Typography';
import { makeStyles } from 'design-system/styles';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { useApiFetchGetRequest } from 'react-scale/hooks/useApiFetchGetRequest';
import { useAsync } from 'react-use-v13';
import QMUpdateDescriptionModal from './QMUpdateDescriptionModal';
import QMUpdatePermissionGroupModal from './QMUpdatePermissionGroupModal';
import Nav from './QMWorkflowSidebarNav';
import { ActionItem, SectionTitle } from './QMWorkflowTypes';
import TeamAssignmentModal from './TeamAssignmentModal';
import { Button as GenAIOpsButton } from '@scale/scaleui';
import { isProjectDisabled } from '@scale/shared/corp/helpers';
import Dialog, { DialogTitle, DialogContent } from 'react-scale/components/design-system/Dialog';
import {
  GenAIOpsHubPaths,
  MainSidebarMenu,
  ProjectConfigMenu,
} from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import ConsoleLinkWithRedirect from 'react-scale/corp/components/Lookup/sections/ConsoleLinkWithRedirect';

const QUICK_LINTER_TASK_TYPES = new Set([
  TaskType.DocumentTranscription,
  TaskType.NamedEntityRecognition,
  TaskType.TextCollection,
  TaskType.ImageAnnotation,
  TaskType.Annotation,
  TaskType.PolygonAnnotation,
  TaskType.PointAnnotation,
  TaskType.LineAnnotation,
  TaskType.VideoPlaybackAnnotation,
  TaskType.VideoAnnotation,
  TaskType.VideoBoxAnnotation,
  TaskType.VideoCuboidAnnotation,
  TaskType.VideoPolygonAnnotation,
]);

const usePermissionGroupDupedNameValidation = (groupName?: string, groupId?: string) => {
  const { value } = useAsync<string[] | undefined>(async () => {
    if (!groupName) return;
    try {
      const query = { search: groupName, exactName: true };
      const result = await apiFetch<IFindPermissionGroupsResponse>(
        null,
        `corp-api/find/permissiongroups`,
        { query },
      );
      return result.permissiongroups.map(group => group._id).filter(id => id !== groupId);
    } catch {
      // do nothing
    }
  }, [groupName, groupId]);
  return value;
};

function CalculateStatusToDisplay(projectIsActive: boolean, projectDisabled: boolean) {
  const classes = useStyles();

  let className;
  let description: string = '';
  let title: string = '';
  if (projectDisabled) {
    className = classes.disabledProject;
    title = 'Disabled';
    description = 'Project is disabled';
  } else if (!projectIsActive) {
    className = classes.noActivityProject;
    title = 'Inactive';
    description = `Project is enabled but hasn't been activity for 2+ weeks. This BLOCKS the queue, can be fixed by "Bumping Project Activitiy" in project's quick actions`;
  } else {
    className = classes.enabledProject;
    title = 'Active';
    description = 'Project is active';
  }

  return {
    title,
    description,
    className,
  };
}

function CalculatePausedStatusToDisplay(projectIsPaused: boolean) {
  const classes = useStyles();

  let className;
  let title: string = '';
  if (projectIsPaused) {
    className = classes.disabledProject;
    title = 'Paused';
  } else {
    className = classes.enabledProject;
    title = 'Non paused';
  }

  return {
    title,
    className,
  };
}

const ProjectStatusDisplay: React.FC<{
  projectId: string;
  isLoading?: boolean;
  projectIsActive: boolean;
  projectDisabled: boolean;
  compact?: boolean;
  hideEditButton: boolean;
  setEditStatusModalOpen: (open: boolean) => void;
}> = ({
  projectId,
  projectIsActive,
  isLoading,
  projectDisabled,
  compact,
  hideEditButton,
  setEditStatusModalOpen,
}) => {
  const { title, description, className } = CalculateStatusToDisplay(
    projectIsActive,
    projectDisabled,
  );

  return (
    <div>
      {!compact && (
        <>
          <Text>
            <strong>PROJECT STATUS </strong>
          </Text>
          {!hideEditButton && (
            <IconButton
              size="small"
              data-tip="Edit status"
              onClick={() => setEditStatusModalOpen(true)}
            >
              <FontAwesomeIcon icon="pencil" color="lightgrey" />
            </IconButton>
          )}
          <br />
        </>
      )}
      {isLoading ? (
        <CircularProgress color="inherit" size="20px" />
      ) : (
        <Tooltip title={description}>
          <div style={{ display: 'flex' }}>
            <div className={className} />
            <Text>{title}</Text>
            <div>
              <a href={`/corp/qm/queue/${projectId}/disable`}> </a>
            </div>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const ProjectPausedStatusDisplay: React.FC<{
  projectIsPaused: boolean;
  isLoading?: boolean;
}> = ({ projectIsPaused, isLoading }) => {
  const { title, className } = CalculatePausedStatusToDisplay(projectIsPaused);

  return (
    <div>
      {isLoading ? (
        <CircularProgress color="inherit" size="20px" />
      ) : (
        <div style={{ display: 'flex' }}>
          <div className={className} />
          <Text>{title}</Text>
        </div>
      )}
    </div>
  );
};

export const PermissionGroupNameDupes: React.FC<{
  name?: string;
  id?: string;
  searchButton?: boolean;
}> = ({ name, id, searchButton }) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const classes = useStyles();
  const dupedGroupNames = usePermissionGroupDupedNameValidation(name, id);
  const message = useMemo(
    () =>
      !dupedGroupNames
        ? ''
        : `Warning: Found ${dupedGroupNames.length} other permission group(s) with the same name`,
    [dupedGroupNames],
  );

  const minLength = useMemo(() => (id ? 0 : 1), [id]);

  if (!name || !dupedGroupNames || dupedGroupNames.length <= minLength) return <></>;
  if (searchButton === true) {
    return (
      <Tooltip title={message}>
        <IconButton
          size="small"
          onClick={() => {
            const url = new URL(window.location.href);
            url.pathname = 'corp/search';
            url.searchParams.set('q', name);
            window.open(url, '_blank');
          }}
        >
          <FontAwesomeIcon icon="engine-warning" color="var(--color-RemoRed)" />
        </IconButton>
      </Tooltip>
    );
  }
  return (
    <div className={classes.smallText}>
      <FontAwesomeIcon icon="engine-warning" color="var(--color-RemoRed)" />
      <i style={{ fontWeight: 'bold' }}> {message}:</i>
      <br />
      <hr />
      {dupedGroupNames.map(id => (
        <a
          key={id}
          style={{ display: 'block' }}
          href={`/${isRemoAdmin ? 'remoadmin/tools' : 'corp'}/lookup/${id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {id}
        </a>
      ))}
    </div>
  );
};

/**
 * We can reuse the component for the clipboard icon!
 */
const QMSidebarClipboard: React.FC<{
  contents: string;
}> = ({ contents }) => (
  <IconButton
    size="small"
    data-tip="Copy to clipboard"
    onClick={() => copyToClipboard(contents, { excludeOuterQuotes: true })}
  >
    <FontAwesomeIcon icon="copy" color="lightgrey" />
  </IconButton>
);

/**
 * Shared sidebar for the Delivery Portal
 */
const QMWorkflowSidebar: React.FC<{
  activeSection?: SectionTitle;
  project?: QMProject;
  setActionItem?: (actionItem: ActionItem | undefined) => void;
}> = props => {
  const [updatePermissionModalOpen, setUpdatePermissionModalOpen] = useState<boolean>(false);
  const [updateDescriptionModalOpen, setUpdateDescriptionModalOpen] = useState<boolean>(false);
  const [showTeamAssignmentDialog, setShowTeamAssignmentDialog] = useState<boolean>(false);
  const [refetchState, setRefetchState] = useState<number>(0);
  const persistedCallout = localStorage.getItem('showCallout') !== 'false';
  const [showCallout, setShowCallout] = useState<boolean>(persistedCallout);
  const [engagementManagersFullNames, setEngagementManagersFullNames] = useState<string[]>([]);
  const [stosFullNames, setStosFullNames] = useState<string[]>([]);
  const [opsDrisFullNames, setOpsDrisFullNames] = useState<string[]>([]);
  const [isLoadingProjectOwners, setIsLoadingProjectOwners] = useState<boolean>(false);
  const [isLoadingShadowProject, setIsLoadingShadowProject] = useState<boolean>(false);
  const [shadowProjectId, setShadowProjectId] = useState<string>('');

  const [isLoadingProjectIsActive, setIsLoadingProjectIsActive] = useState<boolean>(false);
  const [projectIsActive, setProjectIsActive] = useState<boolean>(false);

  const [isLoadingProjectIsPaused, setIsLoadingProjectIsPaused] = useState<boolean>(false);
  const [projectIsPaused, setProjectIsPaused] = useState<boolean>(false);

  const [editStatusModalOpen, setEditStatusModalOpen] = useState<boolean>(false);

  const classes = useStyles();
  const project = props.project;
  const [permissionGroupOfProject, setPermissionGroupOfProject] = useState(
    props.project?.permissionGroup,
  );
  const [projectDescription, setProjectDescription] = useState<string>(
    props.project?.description || '',
  );
  const [isAttemptCompare, setIsAttemptCompare] = useState<boolean>(
    props.project?.metadata?.custom?.isAttemptCompare || false,
  );

  const projectName = props.project?.name;
  const projectId = props.project?.id;
  const subprojectMap = props.project?.subprojectMap;
  const baseProjectId = props.project?.baseProjectId;
  const genAIProjectTypes = props.project?.genAIProjectTypes || [];
  const genAIProjectMediums = props.project?.genAIProjectMediums || [];
  const genAIProjectUseCases = props.project?.genAIProjectUseCases || [];

  useEffect(() => {
    if (!project) {
      return;
    }

    fetchProjectRoles();
    fetchProjectPaused();
    fetchProjectStatus();
    fetchShadowProject();
  }, [project]);

  // We only render the description for GenAI business unit projects
  const isChat =
    project?.taskType === TaskType.Chat ||
    project?.taskType === TaskType.ChatExperimental ||
    project?.taskType === TaskType.ChatV2;

  const router = useRouter();
  const goToProject = (selectedProject: { label: string; value: string } | null) => {
    if (!selectedProject) {
      return;
    }
    const pathname = projectId
      ? router.asPath.replace(projectId, selectedProject.value)
      : '/corp/qm/queue/' + selectedProject.value;
    router.push({
      pathname,
    });
  };

  const assignedTeamsRequest = useApiFetchGetRequest<ListTeamsByProjectsResponse>(
    `corp-api/worker_teams/list_teams_by_project/${projectId}`,
    { refetchState },
  );

  const projectGroups = useApiFetchGetRequest<IProjectGroup[]>(
    `corp-api/project-groups?projectId=${projectId}`,
  );

  const assignedTeamsData = assignedTeamsRequest.response && [
    ...assignedTeamsRequest.response.primary.map(t => ({
      name: t.name,
      tier: 'primary',
      id: t.id,
    })),
    ...assignedTeamsRequest.response.secondary.map(t => ({
      name: t.name,
      tier: 'secondary',
      id: t.id,
    })),
    ...assignedTeamsRequest.response.backup.map(t => ({
      name: t.name,
      tier: 'backup',
      id: t.id,
    })),
  ];

  const collapseIcon = () => {
    return showCallout ? 'chevron-double-up' : 'chevron-double-down';
  };

  const projectDisabled = isProjectDisabled(project?.metadata?.maxCompletableTasks);

  async function fetchProjectRoles() {
    setIsLoadingProjectOwners(true);

    const engagementManagersProjectRoles = await apiFetch<IProjectRole[]>(
      null,
      `corp-api/qm/project_roles/?project=${project?.id}&role=${Role.EM}`,
      {
        method: 'GET',
      },
    );
    const stosProjectRoles = await apiFetch<IProjectRole[]>(
      null,
      `corp-api/qm/project_roles/?project=${project?.id}&role=${Role.STO}`,
      {
        method: 'GET',
      },
    );
    const opsDrisProjectRoles = await apiFetch<IProjectRole[]>(
      null,
      `corp-api/qm/project_roles/?project=${project?.id}&role=${Role.DRI}`,
      {
        method: 'GET',
      },
    );

    const activeEngagementManagersProjectRoles = engagementManagersProjectRoles.filter(
      pr =>
        (!pr.stopDate || moment(pr.stopDate).isAfter(moment())) &&
        moment(pr.startDate).isBefore(moment()),
    );
    const activeStosProjectRoles = stosProjectRoles.filter(
      pr =>
        (!pr.stopDate || moment(pr.stopDate).isAfter(moment())) &&
        moment(pr.startDate).isBefore(moment()),
    );
    const activeOpsDrisProjectRoles = opsDrisProjectRoles.filter(
      pr =>
        (!pr.stopDate || moment(pr.stopDate).isAfter(moment())) &&
        moment(pr.startDate).isBefore(moment()),
    );

    setEngagementManagersFullNames(
      activeEngagementManagersProjectRoles.map(
        em => `${(em.user as IUserJson).firstName} ${(em.user as IUserJson).lastName}`,
      ),
    );
    setStosFullNames(
      activeStosProjectRoles.map(
        sto => `${(sto.user as IUserJson).firstName} ${(sto.user as IUserJson).lastName}`,
      ),
    );
    setOpsDrisFullNames(
      activeOpsDrisProjectRoles.map(
        dri => `${(dri.user as IUserJson).firstName} ${(dri.user as IUserJson).lastName}`,
      ),
    );

    setIsLoadingProjectOwners(false);
  }

  async function fetchProjectPaused() {
    try {
      setIsLoadingProjectIsPaused(true);
      const result: boolean = await apiFetch<boolean, any>(
        null,
        `corp-api/project/${project?.id}/isPaused`,
        {
          method: 'GET',
        },
      );
      setProjectIsPaused(result);
    } catch {
      setProjectIsPaused(false);
    }
    setIsLoadingProjectIsPaused(false);
  }

  async function fetchProjectStatus() {
    try {
      setIsLoadingProjectIsActive(true);
      const result: boolean = await apiFetch<boolean, any>(
        null,
        `corp-api/project/${projectId}/isActive`,
        {
          method: 'GET',
        },
      );
      setProjectIsActive(result);
    } catch {
      setProjectIsActive(false);
    }
    setIsLoadingProjectIsActive(false);
  }

  async function fetchShadowProject() {
    if (project?.projectType === ProjectType.SelfServe) {
      setIsLoadingShadowProject(true);
      try {
        const shadowProject = await apiFetch<string, QMListProjectsShadowProjectRequest>(
          null,
          `corp-api/qm/projects/shadow_project`,
          {
            method: 'POST',
            body: {
              project: project!,
            },
          },
        );
        setShadowProjectId(shadowProject);
      } catch {
        setShadowProjectId('');
      }
      setIsLoadingShadowProject(false);
    }
  }

  async function handleUsePauseOrUnpause(isPauseNewValue: boolean) {
    if (!project || !project.id) {
      return;
    }

    try {
      setIsLoadingProjectIsPaused(true);
      const { isPaused } = await apiFetch<{ isPaused: boolean }>(
        null,
        `corp-api/project/${project.id}/pauseUnpauseProject`,
        {
          method: 'POST',
          body: {
            pause: isPauseNewValue,
          },
        },
      );
      setProjectIsPaused(isPaused);
    } catch {
      // do nothing
    }
    setIsLoadingProjectIsPaused(false);
  }

  return (
    <div className={classes.left}>
      <TeamAssignmentModal
        showTeamAssignmentDialog={showTeamAssignmentDialog}
        setShowTeamAssignmentDialog={setShowTeamAssignmentDialog}
        projectId={project?.id}
        setRefetchState={setRefetchState}
      />
      <div>
        <div className={classes.branding}>
          {projectId && (
            <Link href={`/qm`} style={{ color: 'black' }}>
              <span>
                <Tooltip title={'See all your projects'}>
                  <div>
                    <H1>
                      {' '}
                      <FontAwesomeIcon icon="chevron-left" />
                      <span style={{ marginLeft: '10px' }}>Delivery Portal</span>
                    </H1>
                  </div>
                </Tooltip>
              </span>
            </Link>
          )}
        </div>

        <div className={classes.about}>
          {projectId && (
            <div>
              <div>
                <div className={classes.projectFieldHeader}>
                  <strong style={{ marginTop: '6px', marginBottom: '6px' }}>
                    <Tooltip title={'Go to Delivery Portal Home Page'}>
                      <a href={`/corp/qm/queue/${projectId}`}>
                        <FontAwesomeIcon icon="house" />
                        <span style={{ marginLeft: '5px' }}>Project Delivery Portal Home</span>
                      </a>
                    </Tooltip>
                  </strong>
                  <div className="hsp__1" />
                </div>
              </div>
              <div className={classes.smallText}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <strong className={classes.projectName}>{projectName}</strong>
                  <QMSidebarClipboard contents={projectName!} />
                </div>
              </div>
              <div className={[classes.smallText, classes.projectFieldHeader].join(' ')}>
                <Text className={classes.textBreak}>{projectId}</Text>
                <QMSidebarClipboard contents={projectId} />
              </div>

              {isChat && isAttemptCompare && (
                <div>
                  <Text className={classes.textBreak}>
                    <i>(Attempt Compare)</i>
                  </Text>
                </div>
              )}

              {isChat && (
                <div style={{ marginTop: '6px' }}>
                  <Link
                    target="_blank"
                    href={`${GenAIOpsHubPaths.Projects}/${projectId}/${MainSidebarMenu.Overview}`}
                  >
                    <GenAIOpsButton
                      variant="primary"
                      className="scaleui scale-theme scale-tailwind flex gap-1 items-center "
                      size="lg"
                    >
                      View in GenAI Ops Hub
                    </GenAIOpsButton>
                  </Link>
                </div>
              )}

              {isChat && !!genAIProjectTypes && (
                <div
                  className={[
                    classes.smallText,
                    classes.projectFieldHeader,
                    classes.projectGenAIHeader,
                  ].join(' ')}
                >
                  <Text>
                    <strong>GEN AI PROJECT TYPE(S)</strong>
                    <ul className={classes.projectGenAIUl}>
                      {genAIProjectTypes.map((type, i) => (
                        <li key={`${type}-${i}`}>{type}</li>
                      ))}
                      {genAIProjectTypes.length === 0 && (
                        <li>
                          <i>None</i>
                        </li>
                      )}
                    </ul>
                  </Text>
                  <QMSidebarClipboard contents={genAIProjectTypes.join(', ')} />
                </div>
              )}

              {isChat && !!genAIProjectMediums && (
                <div
                  className={[
                    classes.smallText,
                    classes.projectFieldHeader,
                    classes.projectGenAIHeader,
                  ].join(' ')}
                >
                  <Text>
                    <strong>GEN AI PROJECT MEDIUM(S)</strong>
                    <ul className={classes.projectGenAIUl}>
                      {genAIProjectMediums.map((medium, i) => (
                        <li key={`${medium}-${i}`}>{medium}</li>
                      ))}
                      {genAIProjectMediums.length === 0 && (
                        <li>
                          <i>None</i>
                        </li>
                      )}
                    </ul>
                  </Text>
                  <QMSidebarClipboard contents={genAIProjectMediums.join(', ')} />
                </div>
              )}

              {isChat && !!genAIProjectUseCases && (
                <div
                  className={[
                    classes.smallText,
                    classes.projectFieldHeader,
                    classes.projectGenAIHeader,
                  ].join(' ')}
                >
                  <Text>
                    <strong>GEN AI PROJECT USE CASE(S)</strong>
                    <ul className={classes.projectGenAIUl}>
                      {genAIProjectUseCases.map((useCase, i) => (
                        <li key={`${useCase}-${i}`}>{useCase}</li>
                      ))}
                      {genAIProjectUseCases.length === 0 && (
                        <li>
                          <i>None</i>
                        </li>
                      )}
                    </ul>
                  </Text>
                  <QMSidebarClipboard contents={genAIProjectUseCases.join(', ')} />
                </div>
              )}

              <div className={classes.smallText}>
                {project?.taskType && <ProductPill type={project.taskType} />}
              </div>

              {baseProjectId && (
                <div className={classes.aboutSection}>
                  <Link href={`/lookup/${baseProjectId}`}>BACK TO BASE PROJECT</Link>
                </div>
              )}

              {subprojectMap && (
                <div className={classes.aboutSection}>
                  <div>
                    <Text>
                      <strong>SUBPROJECTS</strong>
                      {Object.entries(subprojectMap).map(([projectName, id]) => (
                        <div className={classes.smallText} key={id}>
                          <Link href={`/lookup/${id}`}>{projectName}</Link>
                        </div>
                      ))}
                    </Text>
                  </div>
                </div>
              )}

              <Dialog open={editStatusModalOpen} onClose={() => setEditStatusModalOpen(false)}>
                <DialogTitle>Edit project status</DialogTitle>
                <DialogContent>
                  <div>
                    <Text>
                      <strong>Current status:</strong>
                      {projectIsPaused ? ' Paused' : ' Not paused'}
                    </Text>
                    <br />
                    <br />
                    <Button
                      disabled={isLoadingProjectIsPaused}
                      className={
                        projectIsPaused ? classes.unpauseProjectButton : classes.pauseProjectButton
                      }
                      onClick={() => {
                        if (confirm('Are you sure you want to change the project status?')) {
                          handleUsePauseOrUnpause(!projectIsPaused);
                          setEditStatusModalOpen(false);
                        }
                      }}
                    >
                      {projectIsPaused ? 'Unpause' : 'Pause'}
                    </Button>
                    <Button
                      style={{ marginLeft: '10px' }}
                      className={classes.pauseProjectButton}
                      onClick={() => {
                        setEditStatusModalOpen(false);
                      }}
                    >
                      Close dialog
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {showCallout && (
                <div>
                  {permissionGroupOfProject && (
                    <div className={classes.aboutSection}>
                      <ProjectStatusDisplay
                        projectId={projectId}
                        isLoading={isLoadingProjectIsActive}
                        projectIsActive={projectIsActive}
                        projectDisabled={projectDisabled}
                        hideEditButton={!isChat}
                        setEditStatusModalOpen={setEditStatusModalOpen}
                      />
                      <ProjectPausedStatusDisplay
                        isLoading={isLoadingProjectIsPaused}
                        projectIsPaused={projectIsPaused}
                      />
                      <div className="sp__1" />
                      <div>
                        <Text>
                          <strong>PERMISSION GROUP</strong>
                        </Text>
                        <Text className={classes.smallText}>(external project name)</Text>
                        <QMSidebarClipboard contents={permissionGroupOfProject.id} />
                      </div>
                      <div className={[classes.smallText, classes.textBreak].join(' ')}>
                        <Text>
                          {permissionGroupOfProject.name} ({permissionGroupOfProject.id})
                        </Text>
                        <IconButton
                          size="small"
                          data-tip="Edit permission group name"
                          onClick={() => setUpdatePermissionModalOpen(true)}
                        >
                          <FontAwesomeIcon icon="pencil" color="lightgrey" />
                        </IconButton>
                      </div>
                      <PermissionGroupNameDupes
                        name={permissionGroupOfProject.name}
                        id={permissionGroupOfProject.id}
                      />

                      <QMUpdatePermissionGroupModal
                        projectId={projectId}
                        customer={project?.taskCustomer.id}
                        isModalOpen={updatePermissionModalOpen}
                        hideModal={(permissionName = '') => {
                          if (permissionName) {
                            setPermissionGroupOfProject({
                              id: permissionGroupOfProject.id,
                              name: permissionName,
                            });
                          }
                          setUpdatePermissionModalOpen(false);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {isChat && (
                <div>
                  <div>
                    <Text>
                      <strong>EXTERNAL DESCRIPTION</strong>
                    </Text>
                  </div>
                  <div
                    className={[classes.smallText, classes.projectFieldHeader].join(' ')}
                    style={{ marginTop: '1vh', marginBottom: '1vh' }}
                  >
                    <Text className={classes.textBreak}>
                      <i>{projectDescription?.length ? projectDescription : 'No Description'}</i>
                    </Text>
                    <QMSidebarClipboard contents={projectDescription} />
                    <IconButton
                      size="small"
                      data-tip="Edit project description name"
                      onClick={() => setUpdateDescriptionModalOpen(true)}
                    >
                      <FontAwesomeIcon icon="pencil" color="lightgrey" />
                    </IconButton>
                    <QMUpdateDescriptionModal
                      projectId={projectId}
                      isModalOpen={updateDescriptionModalOpen}
                      setModalOpen={setUpdateDescriptionModalOpen}
                      taskType={project?.taskType}
                      projectDescription={projectDescription}
                      setProjectDescription={setProjectDescription}
                      isAttemptCompare={isAttemptCompare}
                      setIsAttemptCompare={setIsAttemptCompare}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {showCallout && (
            <div>
              <div className={classes.aboutSection}>
                <div>
                  <Text>
                    <strong>CUSTOMER</strong>
                    {!!project && <QMSidebarClipboard contents={project?.taskCustomer.id} />}
                  </Text>
                </div>
                {project?.taskCustomer.company && (
                  <div className={classes.smallText}>
                    <Text>{project?.taskCustomer.company} </Text>
                  </div>
                )}
                {project?.taskCustomer.email && (
                  <div className={classes.smallText}>
                    <Text>{project?.taskCustomer.email} </Text>
                  </div>
                )}
                <div className={classes.smallText}>
                  <Text>{project?.taskCustomer.id || ''} </Text>
                </div>
                <div className={classes.smallText}>
                  <a
                    href={`/tasks?project=${project?.id}&spoof=${project?.taskCustomer.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Browse Tasks as Customer
                  </a>{' '}
                  /{' '}
                  <ConsoleLinkWithRedirect
                    relativeHref={`/projects/${project?.id}?spoof=${project?.taskCustomer.id}`}
                  />
                </div>
              </div>

              <div className={classes.aboutSection}>
                <div>
                  <Text>
                    <strong>PROJECT OWNERS</strong>
                    {isLoadingProjectOwners ? (
                      <div className={classes.smallText}>
                        <CircularProgress color="inherit" size="20px" />
                      </div>
                    ) : (
                      <>
                        {engagementManagersFullNames.length > 0 ? (
                          <>
                            {engagementManagersFullNames.map((fullName, index) => (
                              <div key={`em-${index}`} className={classes.smallText}>
                                {fullName}{' '}
                                <span style={{ color: 'var(--color-RemoGray50)' }}>{'(EM)'}</span>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className={classes.smallText}>No EM assigned</div>
                        )}
                        {stosFullNames.length > 0 ? (
                          <>
                            {stosFullNames.map((fullName, index) => (
                              <div key={`em-${index}`} className={classes.smallText}>
                                {fullName}{' '}
                                <span style={{ color: 'var(--color-RemoGray50)' }}>{'(STO)'}</span>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className={classes.smallText}>No STO assigned</div>
                        )}
                        {opsDrisFullNames.length > 0 ? (
                          <>
                            {opsDrisFullNames.map((fullName, index) => (
                              <div key={`opsdri-${index}`} className={classes.smallText}>
                                {fullName}{' '}
                                <span style={{ color: 'var(--color-RemoGray50)' }}>
                                  {'(Ops DRI)'}
                                </span>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className={classes.smallText}>No Ops DRI assigned</div>
                        )}
                      </>
                    )}
                  </Text>
                </div>
              </div>

              {project?.projectType === ProjectType.SelfServe && (
                <div className={classes.aboutSection}>
                  <div>
                    <Text>
                      <strong>
                        {project?.name.includes('shadow_project') ? 'MAIN' : 'SHADOW'} PROJECT
                      </strong>
                      {isLoadingShadowProject ? (
                        <div className={classes.smallText}>
                          <CircularProgress color="inherit" size="20px" />
                        </div>
                      ) : shadowProjectId ? (
                        <div className={classes.smallText}>
                          <a
                            href={`/corp/lookup/${shadowProjectId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {shadowProjectId}
                          </a>
                        </div>
                      ) : (
                        <div className={classes.smallText}>Not found</div>
                      )}
                    </Text>
                  </div>
                </div>
              )}

              <div className={classes.aboutSection}>
                <div>
                  <Text>
                    <strong>PROJECT SLA GROUPS</strong>
                    {projectGroups.response &&
                      projectGroups.response.map(({ name, id }) => (
                        <div key={id} className={classes.projectFieldHeader}>
                          <a
                            href={`/corp/project_group/${id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {name}
                          </a>
                        </div>
                      ))}
                    {!projectGroups && <div className={classes.smallText}>Loading...</div>}
                  </Text>
                </div>
              </div>

              <div className={classes.aboutSection}>
                <div>
                  <Text>
                    <strong>WORKER TEAMS</strong>
                    <div
                      className={classes.assignModalButton}
                      onClick={() => {
                        setShowTeamAssignmentDialog(true);
                      }}
                    >
                      Create/Connect Worker Team(s)
                    </div>
                    {assignedTeamsData &&
                      assignedTeamsData.map(({ name, tier, id }) => (
                        <div key={id} className={[classes.smallText, classes.textBreak].join(' ')}>
                          {name} ({tier})
                          <QMSidebarClipboard contents={name} />
                          <IconButton
                            size="small"
                            data-tip="open corp team page"
                            onClick={() => window.open(`/corp/team/${id}`, '_blank')}
                          >
                            <FontAwesomeIcon icon="share-square" color="#73C2FB" />
                          </IconButton>
                        </div>
                      ))}
                    {assignedTeamsData && assignedTeamsData.length === 0 && (
                      <div className={classes.smallText}>
                        No worker teams assigned to this project
                      </div>
                    )}
                    {!assignedTeamsData && <div className={classes.smallText}>Loading...</div>}
                  </Text>
                </div>
              </div>
              <div className={classes.aboutSection}>
                <div>
                  <strong>QUALIFICATION LISTS</strong>
                  <a
                    href={`${GenAIOpsHubPaths.Projects}/${projectId}/${MainSidebarMenu.ContributorManagement}/${ProjectConfigMenu.Qualifications}`}
                  >
                    <IconButton size="small" data-tip="Edit project qualification lists">
                      <FontAwesomeIcon icon="pencil" color="lightgrey" />
                    </IconButton>
                  </a>
                  <div className={classes.smallText}>
                    {project?.qualificationListIds?.length
                      ? project.qualificationListIds.map(qualificationList => (
                          <Fragment key={qualificationList}>
                            {qualificationList}
                            <IconButton
                              size="small"
                              data-tip="Copy to clipboard"
                              onClick={() =>
                                copyToClipboard(qualificationList, {
                                  excludeOuterQuotes: true,
                                })
                              }
                            >
                              <FontAwesomeIcon icon="copy" color="lightgrey" />
                            </IconButton>
                          </Fragment>
                        ))
                      : 'None'}
                  </div>
                  <strong>WORKER SKILLS</strong>
                  <div className={classes.smallText}>
                    {project?.workerSkillNames?.length
                      ? JSON.stringify(project?.workerSkillNames)
                      : 'None'}
                  </div>
                </div>
              </div>
              <div className={classes.aboutSection}>
                <a href={`/corp/qm/queue/${projectId}/config`}>Full Configs</a>
              </div>
              {projectId && (
                <Link href={`/lookup/${projectId}?preventRedirect=true`}>
                  <Button className={classes.linkButtons}>Back to old lookup</Button>
                </Link>
              )}
            </div>
          )}
          <div className={classes.showCallout}>
            <a
              href="#"
              style={{ color: 'black' }}
              onClick={() => {
                try {
                  localStorage.setItem('showCallout', (!showCallout).toString());
                } catch {
                  // prevent erroring-out when out of localstorage
                }
                setShowCallout(current => !current);
              }}
            >
              {showCallout ? 'Collapse' : 'More'}
              <FontAwesomeIcon style={{ marginLeft: '5px' }} icon={collapseIcon()} />
            </a>
          </div>
        </div>

        <div className={classes.switchProject}>
          <H3>Switch Project</H3>
          <SelectProject onChange={goToProject} showProjectId={true} width="100%" />
        </div>

        {props.project && (
          <Nav
            projectId={props.project.id}
            taskType={props.project.taskType}
            taskTemplateVersion={props.project.taskTemplateVersion}
            projectType={props.project.projectType}
            hasBaseProject={!!props.project.baseProjectId}
            activeScrollName={props.activeSection}
            setActionItem={props.setActionItem}
            quickLinterTaskTypes={QUICK_LINTER_TASK_TYPES}
            usesPipelineV3={props.project.usesPipelineV3}
          />
        )}
      </div>
    </div>
  );
};

const statusProjectIcon = {
  marginTop: '2.5px',
  marginRight: '5px',
  padding: '5px',
  paddingRight: '2.5px',
  borderRadius: '50%',
  width: '12px',
  height: '12px',
};

const useStyles = makeStyles({
  left: {
    width: 270, // Just long enough to show the project ID
    height: '100%',
    flexShrink: 0,
    zIndex: 1,
    top: 100,
    overflow: 'auto',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingRight: 8,
    // Hack because CorpLayout has padding
    marginLeft: -24,
    marginTop: -16,
  },
  branding: {
    background: 'white',
    padding: 4,
    display: 'flex',
    width: '100%',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 150ms ease-out',
    '&:hover': {
      background: '#f2f2f2',
    },
    marginBottom: 10,
  },
  projectFieldHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  projectGenAIHeader: {
    marginTop: '8px',
    marginBottom: '8px',
  },
  projectGenAIUl: {
    margin: 'auto',
    paddingLeft: '16px',
  },
  about: {
    background: 'var(--color-RemoGray01)',
    borderRadius: 8,
    padding: '8px 16px',
    overflow: 'hidden',
  },
  smallText: {
    fontSize: 12,
  },
  showCallout: {
    fontSize: 12,
    textAlign: 'right',
    color: 'black',
  },
  aboutSection: {
    marginTop: 8,
  },
  callout: {
    background: 'var(--color-RemoGray01)',
    padding: 10,
    borderRadius: 8,
    marginTop: 24,
  },
  linkButtons: {
    width: '100%',
    marginTop: 12,
  },
  textBreak: {
    wordBreak: 'break-all',
    overflow: 'hidden',
    display: 'inline-block',
  },
  projectName: {
    wordBreak: 'break-all',
    overflow: 'hidden',
    display: 'inline-block',
    fontSize: 'larger',
  },
  switchProject: {
    marginTop: 8,
  },
  projectStatusContainer: {
    textAlign: 'center',
  },
  disabledProject: {
    ...statusProjectIcon,
    backgroundColor: 'var(--color-RemoRed)',
  },
  noActivityProject: {
    ...statusProjectIcon,
    backgroundColor: 'var(--color-RemoGold)',
  },
  enabledProject: {
    ...statusProjectIcon,
    backgroundColor: 'var(--color-RemoGreen)',
  },
  pauseProjectButton: {
    backgroundColor: 'var(--color-RemoRed)',
    color: 'white',
  },
  unpauseProjectButton: {
    backgroundColor: 'var(--color-RemoGreen)',
    color: 'white',
  },
  assignModalButton: {
    color: 'var(--color-RemoGreen)',
    fontWeight: 'bold',
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  },
});

export default QMWorkflowSidebar;
